import React, { useState } from "react";
import {
  Dumbbell,
  Activity,
  Footprints,
  Wind,
  PersonStanding,
  Sword,
  Plus,
  Edit3,
  Trash2,
  Check,
  Zap,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { sfx } from "../lib/sounds";

const ICONS = {
  Dumbbell,
  Activity,
  Footprints,
  Wind,
  PersonStanding,
  Sword,
  Zap,
};

const ICON_OPTIONS = Object.keys(ICONS);

export default function QuestList({
  quests,
  onToggle,
  onAdd,
  onEdit,
  onRemove,
  onCompleteDay,
}) {
  const [editing, setEditing] = useState(null); // quest or null
  const [adding, setAdding] = useState(false);

  const allDone = quests.length > 0 && quests.every((q) => q.done);
  const anyDone = quests.some((q) => q.done);
  const totalXpReady = quests
    .filter((q) => q.done)
    .reduce((a, q) => a + q.xp, 0);

  return (
    <div
      className="relative border border-white/10 bg-[#0A0D14] p-5 md:p-6"
      data-testid="quest-list-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400/80">
            // Daily Directive
          </div>
          <h2 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight mt-1">
            Today&apos;s Quests
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Resets at 00:00 · Miss a day, lose your streak.
          </p>
        </div>
        <Button
          variant="outline"
          className="border-purple-500/40 text-purple-200 bg-purple-500/5 hover:bg-purple-500/15 hover:text-white rounded-none gap-1.5"
          onClick={() => {
            sfx.click();
            setAdding(true);
          }}
          data-testid="add-quest-button"
        >
          <Plus className="h-4 w-4" /> New Quest
        </Button>
      </div>

      <ul className="mt-5 space-y-2" data-testid="quest-list">
        {quests.length === 0 && (
          <li className="text-center text-sm text-slate-500 py-10 border border-dashed border-white/10">
            No quests assigned. Add one to begin your training.
          </li>
        )}
        {quests.map((q) => {
          const Icon = ICONS[q.icon] || Sword;
          return (
            <li
              key={q.id}
              className={`group flex items-center gap-3 border ${
                q.done
                  ? "border-purple-500/30 bg-purple-500/5"
                  : "border-white/10 bg-black/30 hover:border-blue-500/40"
              } transition p-3`}
              data-testid={`quest-item-${q.id}`}
            >
              <button
                onClick={() => {
                  if (!q.done) sfx.questComplete();
                  else sfx.questUncheck();
                  onToggle(q.id);
                }}
                className={`relative h-7 w-7 shrink-0 border ${
                  q.done
                    ? "bg-purple-500 border-purple-400"
                    : "border-white/30 hover:border-blue-400"
                } flex items-center justify-center`}
                data-testid={`quest-toggle-${q.id}`}
                aria-label="Toggle quest"
              >
                {q.done && <Check className="h-4 w-4 text-white" />}
              </button>

              <Icon
                className={`h-5 w-5 shrink-0 ${
                  q.done ? "text-purple-300" : "text-blue-300"
                }`}
              />

              <div className="flex-1 min-w-0">
                <div
                  className={`font-display uppercase tracking-wider text-sm md:text-base ${
                    q.done ? "text-slate-500 line-through" : "text-white"
                  }`}
                >
                  {q.title}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">
                  Reward · <span className="text-amber-300">+{q.xp} XP</span>
                </div>
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => setEditing(q)}
                  className="p-1.5 hover:bg-white/10"
                  data-testid={`quest-edit-${q.id}`}
                  aria-label="Edit quest"
                >
                  <Edit3 className="h-3.5 w-3.5 text-slate-400" />
                </button>
                <button
                  onClick={() => {
                    sfx.click();
                    onRemove(q.id);
                  }}
                  className="p-1.5 hover:bg-red-500/20"
                  data-testid={`quest-delete-${q.id}`}
                  aria-label="Delete quest"
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-300" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="text-xs text-slate-400 font-mono flex-1">
          {anyDone ? (
            <>
              <span className="text-purple-300">
                +{totalXpReady} XP
              </span>{" "}
              awaiting collection
            </>
          ) : (
            "Mark quests as complete to gather XP."
          )}
        </div>
        <button
          disabled={!anyDone}
          onClick={() => {
            if (!anyDone) return;
            sfx.completeDay();
            onCompleteDay();
          }}
          className={`relative font-display uppercase tracking-[0.2em] text-sm font-bold px-7 py-3.5 border-2 transition ${
            anyDone
              ? "border-purple-400 bg-purple-600 text-white hover:bg-purple-500 active:scale-95"
              : "border-white/10 bg-white/5 text-slate-600 cursor-not-allowed"
          } ${allDone ? "animate-pulse-glow" : ""}`}
          data-testid="complete-day-button"
        >
          Complete Day
        </button>
      </div>

      {/* Add Quest Dialog */}
      <QuestDialog
        open={adding}
        onOpenChange={setAdding}
        title="Forge New Quest"
        onSubmit={(data) => {
          onAdd(data);
          setAdding(false);
        }}
      />

      {/* Edit Quest Dialog */}
      <QuestDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title="Modify Quest"
        initial={editing}
        onSubmit={(data) => {
          onEdit(editing.id, data);
          setEditing(null);
        }}
      />
    </div>
  );
}

function QuestDialog({ open, onOpenChange, title, initial, onSubmit }) {
  const [name, setName] = useState(initial?.title || "");
  const [xp, setXp] = useState(initial?.xp || 50);
  const [icon, setIcon] = useState(initial?.icon || "Sword");

  React.useEffect(() => {
    if (open) {
      setName(initial?.title || "");
      setXp(initial?.xp || 50);
      setIcon(initial?.icon || "Sword");
    }
  }, [open, initial]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0D14] border border-purple-500/40 rounded-none text-white max-w-md"
        data-testid="quest-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl text-glow-purple">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
              Quest Name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="50 Burpees"
              className="mt-1 bg-black/50 border-white/10 rounded-none focus-visible:ring-purple-500"
              data-testid="quest-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                XP Reward
              </Label>
              <Input
                type="number"
                min={1}
                value={xp}
                onChange={(e) => setXp(parseInt(e.target.value) || 0)}
                className="mt-1 bg-black/50 border-white/10 rounded-none focus-visible:ring-purple-500"
                data-testid="quest-xp-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                Icon
              </Label>
              <Select value={icon} onValueChange={setIcon}>
                <SelectTrigger
                  className="mt-1 bg-black/50 border-white/10 rounded-none"
                  data-testid="quest-icon-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0D14] border-white/10 rounded-none text-white">
                  {ICON_OPTIONS.map((k) => {
                    const Ico = ICONS[k];
                    return (
                      <SelectItem
                        key={k}
                        value={k}
                        className="rounded-none focus:bg-purple-500/20"
                      >
                        <div className="flex items-center gap-2">
                          <Ico className="h-4 w-4" />
                          {k}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none border-white/20 text-slate-300 bg-transparent hover:bg-white/5"
            data-testid="quest-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || xp <= 0}
            onClick={() => onSubmit({ title: name.trim(), xp, icon })}
            className="rounded-none bg-purple-600 hover:bg-purple-500"
            data-testid="quest-dialog-save"
          >
            Save Quest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
